// 203 §B.2 [A CURE — its own commit, before the improvement] — THE OVERHEADS
// SHEET'S PURE HELPERS, MOVED here from src/views/CostsView.tsx (the block
// between the 161 §9 comment and `OH_MONTH_BOX`: the two types, the twelve
// months, the ONE whole-euro formatter and its two faces, the digits reader and
// E.5a's spread) so that the sheet's importer (§B.2–B.5, its own file) reads
// THE SAME definitions the sheet renders and spreads with — never a second
// formatter, never a second spread. Bodies byte-identical to the source lines
// (`export ` prefixed, nothing else); the sheet imports them back. The rules
// they implement — 198 §E (whole euros, the faces) and 161 §9 E.4–E.6 (the
// total first, the total an input, the total never stored) — stay documented
// on the sheet, which is the surface they rule.
// 203 §B.3 — widened by the four label columns the importer matches a row on (rule 2: the union of what is read)
export type OhCat = { code: string; order?: number | null; archived?: boolean; label?: string | null; labelEn?: string | null; labelFr?: string | null; labelNl?: string | null };
export type OhSheetJson = Record<string, Record<string, Record<string, number>>>;
export const MONTHS = Array.from({ length: 12 }, (_, i) => i + 1);
/** the ONE whole-euro formatter: de-DE grouping (a dot between thousands), no decimal part */
export const INT_DE = new Intl.NumberFormat("de-DE", { maximumFractionDigits: 0, minimumFractionDigits: 0 });
/** the case table's whole-euro face (CoView's `__fcCo`, hoisted): « 1.234 € », "—" for nothing */
export const fcWhole = (v: number | null | undefined): string => (v == null ? "—" : `${INT_DE.format(Math.floor(v))} €`);
/** the same digits inside an input box — no sign; a stored cent value shows its floor, as the tables do */
export const intFace = (v: number | null): string => (v == null ? "" : INT_DE.format(Math.floor(v)));
/** what was typed, as whole euros; nothing typed → null.
 *  208 §10 — THE COMMA IS THE DECIMAL SEPARATOR, and the amount is ROUNDED TO THE
 *  EURO: « 76,5 » is 77 (it read the digits alone, « 76,5 » became 765). The dot
 *  stays the sheet's thousands — its own face, « 125.000 » (198 E.0 [RULED]: "round
 *  it to the euro everywhere, no cents … a dot between thousands"), which is why
 *  the sheet keeps this reader rather than the one amount field (two decimals, a
 *  space between thousands). Anything else typed is dropped, as before. */
export const digitsOf = (raw: string): number | null => {
  const t = raw.replace(/[^\d,]/g, "");
  if (!/\d/.test(t)) return null;
  const i = t.indexOf(",");
  const whole = i === -1 ? t : t.slice(0, i);
  const frac = i === -1 ? "" : t.slice(i + 1).replace(/,/g, "");
  return Math.round(Number(`${whole || "0"}.${frac || "0"}`));
};
/** E.5a — n euros over the twelve months, evenly, the remainder one each to the EARLIEST months */
export const spreadEvenly = (n: number): number[] => {
  const base = Math.floor(n / 12);
  const rem = n - base * 12;
  return MONTHS.map((_, i) => base + (i < rem ? 1 : 0));
};

/* 203 §B.5 — READ BY THE SHEET AND BY ITS IMPORTER'S PREVIEW: the figures the
   reader confirms before a save are THE APP'S OWN, through the definitions the
   sheet renders with — one builder per figure, never a second sum. */
/** one year of the stored shape: { "<code>": { "<month 1-12>": amount } } */
export type OhYear = Record<string, Record<string, number>>;
/** a stored cell, or null: a finite number or nothing (the sheet's own guard) */
export const yearCell = (y: OhYear | null | undefined, code: string, m: number): number | null => {
  const v = y?.[code]?.[String(m)];
  return typeof v === "number" && Number.isFinite(v) ? v : null;
};
/** a month's column total over the categories on screen — the sheet's `colTotal` */
export const yearColTotal = (y: OhYear | null | undefined, codes: readonly string[], m: number): number => codes.reduce((s, code) => s + (yearCell(y, code, m) ?? 0), 0);
/** the year's grand total — the sheet's `grand` */
export const yearGrand = (y: OhYear | null | undefined, codes: readonly string[]): number => MONTHS.reduce((s, m) => s + yearColTotal(y, codes, m), 0);
